import React from 'react'
import { injectIntl, Link } from 'gatsby-plugin-react-intl'
import { useStaticQuery, graphql } from 'gatsby'
import styled from 'styled-components'
import { color, breakpoint, transition } from '@configs/utilities'
import logo from '@assets/images/footer/logo.svg'
import { FaFacebookSquare } from 'react-icons/fa'
import { FaInstagramSquare } from 'react-icons/fa'
import { FaLinkedin } from 'react-icons/fa'
import AccordionMobileFooter from '@components/ui/accordions/AccordionMobileFooter'
import SectionWrapper from '@components/ui/wrappers/SectionWrapper'
import { OpenModalContext } from '@components/layouts/default/Layout'
import modalContactTypes from '@configs/modalContactTypes'

const Footer = ({ intl }) => {
  const data = useStaticQuery(graphql`
    query {
      allFooterJson {
        edges {
          node {
            head
            submenu {
              path
              text
            }
            add
          }
        }
      }
    }
  `)

  return (
    <SectionWrapper sectionBackground={color.blueDark} hidePaddingBottom>
      <OpenModalContext.Consumer>
        {(value) => {
          return (
            <FooterWrapper>
              <FooterContentSection>
                <FooterLogoSection>
                  <Link to="/">
                    <img src={logo} alt="Footer Logo" />
                  </Link>
                  <FooterLogoText>{intl.formatMessage({ id: 'footer.head' })}</FooterLogoText>
                </FooterLogoSection>
                <FooterMainSection>
                  {data.allFooterJson.edges.map((item, index) => {
                    return (
                      <FooterMainColumn key={index}>
                        <MdFooter>
                          <MainHeads>{intl.formatMessage({ id: item.node.head })}</MainHeads>
                          <MainLinks>
                            {item.node.submenu.map((item, index) => {
                              return (
                                <SubmenuLinkWrapper key={index}>
                                  {item.path.includes('https') ? (
                                    <a href={item.path} target="_self" rel="noopener noreferrer">
                                      {intl.formatMessage({ id: item.text })}
                                    </a>
                                  ) : item.path !== 'contact' ? (
                                    <Link to={item.path}>{intl.formatMessage({ id: item.text })}</Link>
                                  ) : (
                                    <span
                                      onClick={() => {
                                        value.openContact(modalContactTypes.CONTACT)
                                      }}
                                      className="contact"
                                      aria-hidden="true"
                                    >
                                      {intl.formatMessage({ id: item.text })}
                                    </span>
                                  )}
                                </SubmenuLinkWrapper>
                              )
                            })}
                          </MainLinks>
                        </MdFooter>

                        <SmFooter>
                          <AccordionMobileFooter
                            id={index}
                            head={intl.formatMessage({ id: item.node.head })}
                            text={
                              <MainLinks>
                                {item.node.submenu.map((item, index) => {
                                  return (
                                    <SubmenuLinkWrapper key={index}>
                                      {item.path.includes('https') ? (
                                        <a href={item.path} target="_self" rel="noopener noreferrer">
                                          {intl.formatMessage({ id: item.text })}
                                        </a>
                                      ) : item.path !== 'contact' ? (
                                        <Link to={item.path}>{intl.formatMessage({ id: item.text })}</Link>
                                      ) : (
                                        <span
                                          onClick={() => {
                                            value.openContact(modalContactTypes.CONTACT)
                                          }}
                                          className="contact"
                                          aria-hidden="true"
                                        >
                                          {intl.formatMessage({ id: item.text })}
                                        </span>
                                      )}
                                    </SubmenuLinkWrapper>
                                  )
                                })}
                              </MainLinks>
                            }
                          />
                        </SmFooter>

                        {item.node.add ? (
                          <FooterFollowSection>
                            <MainHeads>{intl.formatMessage({ id: item.node.add })}</MainHeads>
                            <FooterFollowIconWrapper>
                              <a href="https://facebook.com/YourIdeaBuddy" target="_blank" rel="noopener noreferrer">
                                <FaFacebookSquare size={'20px'} />
                              </a>
                              <a href="https://www.instagram.com/ideabuddy/" target="_blank" rel="noopener noreferrer">
                                <FaInstagramSquare size={'20px'} />
                              </a>
                              <a
                                href="https://www.linkedin.com/company/ideabuddy/"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaLinkedin size={'20px'} />
                              </a>
                            </FooterFollowIconWrapper>
                          </FooterFollowSection>
                        ) : null}
                      </FooterMainColumn>
                    )
                  })}
                </FooterMainSection>
              </FooterContentSection>
              <FooterBottomSection>
                <FooterBottomIdea>
                  <span>
                    © IdeaBuddy {new Date().getFullYear()}, {intl.formatMessage({ id: 'footer.ideaBuddy' })}
                  </span>
                </FooterBottomIdea>
                <FooterBottomLinks>
                  <Link to="/terms-of-service">{intl.formatMessage({ id: 'footer.terms' })}</Link>
                  <span>|</span>
                  <Link to="/privacy-policy">{intl.formatMessage({ id: 'footer.privacy' })}</Link>
                  <span>|</span>
                  <Link to="/accessibility-policy">{intl.formatMessage({ id: 'footer.accessibility' })}</Link>
                  <span>|</span>
                  <Link to="/cookie-statement">{intl.formatMessage({ id: 'footer.cookies' })}</Link>
                </FooterBottomLinks>
              </FooterBottomSection>
            </FooterWrapper>
          )
        }}
      </OpenModalContext.Consumer>
    </SectionWrapper>
  )
}

export default injectIntl(Footer)

const FooterWrapper = styled.div`
  padding: 20px 0 0;

  a,
  .contact {
    color: ${color.footerLink};
    font-weight: 300;
    font-size: 14px;
    transition: ${transition.default};
    cursor: pointer;
    @media (hover: hover) {
      &:hover {
        opacity: 0.7;
      }
    }
  }
`

const FooterContentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  @media screen and (min-width: ${breakpoint.lg}) {
    flex-direction: row;
    column-gap: 15px;
  }
`

/* ---------------------------------------------- LOGO SECTION ---------------------------------------------- */

const FooterLogoSection = styled.div`
  width: 100%;

  @media screen and (min-width: ${breakpoint.lg}) {
    width: 25%;
  }
`

const FooterLogoText = styled.p`
  width: 100%;
  font-weight: 300;
  font-size: 14px;
  line-height: 22px;
  color: ${color.white};
  margin-top: 25px;

  @media screen and (min-width: ${breakpoint.lg}) {
    width: 250px;
    font-size: 16px;
  }
`

/* ---------------------------------------------- MAIN SECTION ---------------------------------------------- */

const FooterMainSection = styled.div`
  display: flex;
  padding: 50px 0 0 0;
  flex-direction: column;

  @media screen and (min-width: ${breakpoint.lg}) {
    flex-direction: row;
  }

  @media screen and (min-width: ${breakpoint.lg}) {
    justify-content: space-between;
  }

  @media screen and (min-width: ${breakpoint.lg}) {
    padding: 0;
    flex-grow: 1;
  }
`

const FooterMainColumn = styled.div`
  width: 100%;

  // @media screen and (min-width: ${breakpoint.sm}) {
  //   width: 25%;
  // }

  @media screen and (min-width: ${breakpoint.lg}) {
    width: 20%;
    padding: 0 5px;
  }

  @media screen and (min-width: ${breakpoint.xl}) {
    width: 20%;
    padding: 0 20px;
  }
`

const MdFooter = styled.div`
  display: none;

  @media screen and (min-width: ${breakpoint.lg}) {
    display: block;
  }
`

const SmFooter = styled.div`
  display: block;

  @media screen and (min-width: ${breakpoint.lg}) {
    display: none;
  }
`

const FooterFollowSection = styled.div`
  margin-top: 35px;

  img {
    margin-right: 20px;
  }
`

const FooterFollowIconWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 20px;
`

const SubmenuLinkWrapper = styled.div`
  display: flex;
`

const MainHeads = styled.p`
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${color.white};
  margin: 0 0 20px 0;

  @media screen and (min-width: ${breakpoint.md}) {
    margin: 15px 0;
  }
`

const MainLinks = styled.div`
  display: flex;
  flex-direction: column;

  a,
  .contact {
    margin-bottom: 16px;
    width: max-content;
  }
`

/* ---------------------------------------------- BOTTOM SECTION ---------------------------------------------- */

const FooterBottomSection = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  align-items: center;
  border-top: 1px solid ${color.footerBorder};
  row-gap: 15px;
  margin-top: 50px;
  padding: 24px 0;

  @media screen and (min-width: ${breakpoint.md}) {
    justify-content: space-between;
    flex-direction: row;
  }
`

const FooterBottomIdea = styled.div`
  text-align: center;

  span {
    font-weight: 300;
    font-size: 14px;
    line-height: 20px;
    color: ${color.white};
  }
`

const FooterBottomLinks = styled.div`
  text-align: center;

  span {
    color: ${color.footerLink};
    margin: 0 10px;
  }
`
